import React from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { candidateSchema, ICandidate } from "../../data/Candidates";
import CandidateContactForm from "./CandidateContactForm";
import CandidateBioForm from "./CandidateBioForm";
import NotesForm from "../misc/NotesForm";
import { useSnackbar } from "notistack";

const defaultValues: ICandidate = {
  name: "",
  title: null,
  id_number: "",
  phone_number_primary: "",
  phone_number_secondary: "",
  email: "",
  email_secondary: "",
  address: "",
  location: null,
  notice_period: 0,
  gender: null,
  qualifies_for_bee: false,
  valid_work_permit: false,
  permanent_resident: false,
  has_own_car: false,
  salary: "",
  nationality: null,
  driving_licence: null,
  areas: [],
  languages: [],
  qualifications: [],
  notes: [],
};

export default function CandidateCreate() {
  React.useEffect(() => {
    document.title = "Candidates";
  }, []);

  const { enqueueSnackbar } = useSnackbar();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: ICandidate) =>
      axios.post<ICandidate>("/candidates/", data).then((r) => r.data),
  });

  const methods = useForm<ICandidate>({
    disabled: isPending,
    defaultValues,
    resolver: yupResolver(candidateSchema),
  });
  const { reset, handleSubmit } = methods;

  const handleCreate: SubmitHandler<ICandidate> = (data) => {
    mutate(data, {
      onSuccess: (responseData) => {
        reset(defaultValues);
        enqueueSnackbar(`${responseData.name} created.`, {
          action: (
            <IconButton
              component={RouterLink}
              to={`/candidates/${responseData.id}`}
            >
              <EditIcon sx={{ color: "white" }} />
            </IconButton>
          ),
          variant: "success",
        });
      },
      onError: () => enqueueSnackbar("Failed to create candidate.", { variant: "error" }),
    });
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(handleCreate)}
        autoComplete="off"
        sx={{
          display: "grid",
          columnGap: 3,
          ml: 3,
          mr: 3,
          gridTemplateRows: `auto 1fr`,
          gridTemplateColumns: `1fr 1fr 1fr 1fr`,
          gridTemplateAreas: `
                  "toolbar toolbar toolbar toolbar"
                  "candidate bio notes _"
                  `,
        }}
      >
        <Box
          sx={{
            gridArea: "toolbar",
            borderBottom: 1,
            borderColor: "grey.300",
          }}
        >
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              CANDIDATE
            </Typography>
            <Button
              type="submit"
              startIcon={<SaveIcon />}
              loading={isPending}
              loadingPosition="start"
              variant="contained"
            >
              Save
            </Button>
          </Toolbar>
        </Box>
        <Box sx={{ gridArea: "candidate" }}>
          <CandidateContactForm />
        </Box>
        <Box sx={{ gridArea: "bio" }}>
          <CandidateBioForm />
        </Box>
        <Box sx={{ gridArea: "notes" }}>
          <NotesForm disabled={isPending} />
        </Box>
      </Box>
    </FormProvider>
  );
}
